import type { AuthUser } from "@dentacore/shared-types";

import styles from "./session-switched.module.css";

/**
 * The tab noticed it is no longer the person who was using it.
 *
 * **Why this is a screen and not a banner** — see the CSS. While this is up,
 * the tab holds a valid token belonging to `nowSignedIn`, so every screen
 * behind it would be that person's data read by someone who believes they are
 * still themselves. Clinical notes are append-only and attributed to whoever is
 * signed in, so the cost of guessing wrong is a note in a patient's permanent
 * file under the wrong clinician's name.
 *
 * **Both choices are honest.** Continuing is legitimate — the session is real
 * and the person probably did just sign in next door. Signing out is for the
 * case where they did not, and it is listed second rather than hidden.
 */
export function SessionSwitched({
  nowSignedIn,
  onContinue,
  onSignOut,
}: {
  /**
   * Null when the switch was noticed live. Finding out who it is costs a spend
   * of a single-use refresh token, which would read as a replay and revoke both
   * sessions — see the `storage` listener. Unnamed and honest beats named and
   * destructive.
   */
  nowSignedIn: AuthUser | null;
  onContinue: () => void;
  onSignOut: () => void;
}): React.JSX.Element {
  const name =
    nowSignedIn === null ? null : `${nowSignedIn.firstName} ${nowSignedIn.lastName}`.trim();

  return (
    <div
      className={styles.scrim}
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="switch-title"
    >
      <div className={styles.panel}>
        <p className={styles.eyebrow}>Session changed</p>
        <h1 className={styles.title} id="switch-title">
          This tab is now signed in as someone else
        </h1>
        <p className={styles.body}>
          {name === null ? (
            <>Somebody signed in from another tab or window.</>
          ) : (
            <>
              Somebody signed in as <span className={styles.who}>{name}</span> in another tab or
              window.
            </>
          )}{" "}
          A browser holds one DentaCore session per practice, so this tab has moved with it.
        </p>
        <p className={styles.body}>
          Anything you do from here — notes, bookings, charting — is recorded against{" "}
          {name === null ? "whoever that is" : <span className={styles.who}>{name}</span>}, not
          against you.
        </p>
        <div className={styles.actions}>
          <button type="button" className={styles.primary} onClick={onContinue} autoFocus>
            {name === null ? "Continue as the new user" : `Continue as ${name}`}
          </button>
          <button type="button" className={styles.secondary} onClick={onSignOut}>
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
}
